/**
 * The hero when it plays one of the trainer's YouTube materials instead of the demo — variant D of
 * the 25 September 2026 canvas („Ćwiczenia: YouTube w podglądzie", boards D1–D3).
 *
 * ── The same box as the hero, so choosing a tab moves nothing ───────────────────────────────
 * `flex: 1` over the hero's own floor. The demo's `TheatreHero` is unmounted while this stands,
 * which is also what stops the demo: there is never a second film playing under this one.
 *
 * ── The frame is YouTube's privacy-enhanced embed, built from an id ─────────────────────────
 * `youtubeEmbedUrl` composes the address; the trainer's url never reaches `src`. The frame is
 * sandboxed to what the player needs — scripts and its own origin to run, presentation for full
 * screen, popups so its own „watch on YouTube" leaves in a tab rather than inside the card.
 *
 * ── An owner's refusal is DRAWN, not left to YouTube's grey box ─────────────────────────────
 * With `enablejsapi` the player reports its errors by `postMessage` once the page says it is
 * listening — no YouTube script is loaded here, which is what keeps `script-src 'self'` intact. A
 * refusal (`youtubeRefusal`) swaps the frame for the film's own still under a dim, one sentence, and
 * the link out. The refusal is only known after the frame answers: nothing about a url says in
 * advance whether its owner allows embedding.
 *
 * ── A Short stands upright ──────────────────────────────────────────────────────────────────
 * 9:16 in the middle of the box, on the player's near-black stage. Never cropped.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  LinkBrandMark,
  YOUTUBE_EMBED_ORIGIN,
  youtubeEmbedUrl,
  youtubeRefusal,
  youtubeThumbnailUrl,
  type YouTubeVideo,
} from '~/entities/exercise-library';

import { ExternalGlyph } from './ExerciseLibraryIcons';
import styles from './TheatreYouTube.module.css';

interface TheatreYouTubeProps {
  readonly video: YouTubeVideo;
  /** The material's own words, or its host where the trainer gave none. */
  readonly label: string;
  /** The material's address, for the way out. Already through the link gate. */
  readonly url: string;
  /** The tab panel this stage is, so the source tabs' `aria-controls` lands on it. */
  readonly panelId: string;
  readonly tabId: string;
}

export function TheatreYouTube({ video, label, url, panelId, tabId }: TheatreYouTubeProps) {
  const { t } = useTranslation();
  const frame = useRef<HTMLIFrameElement>(null);
  const [refused, setRefused] = useState(false);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== YOUTUBE_EMBED_ORIGIN) return;
      if (event.source !== frame.current?.contentWindow) return;
      if (youtubeRefusal(event.data)) setRefused(true);
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, []);

  return (
    <div className={styles.stage} role="tabpanel" id={panelId} aria-labelledby={tabId}>
      {refused ? (
        <>
          <img className={styles.still} src={youtubeThumbnailUrl(video)} alt="" decoding="async" />
          <div className={styles.refusal}>
            <span className={styles.refusalMark}>
              <LinkBrandMark brand="youtube" className={styles.refusalGlyph} />
            </span>
            <p className={styles.refusalTitle}>{t('web:exerciseLibrary.theatre.youtube.refusedTitle')}</p>
            <p className={styles.refusalBody}>{t('web:exerciseLibrary.theatre.youtube.refusedBody')}</p>
            <a className={styles.out} href={url} target="_blank" rel="noreferrer noopener">
              {t('web:exerciseLibrary.theatre.youtube.openOnYouTube')}
              <ExternalGlyph className={styles.outGlyph} />
            </a>
          </div>
        </>
      ) : (
        <iframe
          ref={frame}
          className={video.vertical ? styles.frameUpright : styles.frame}
          src={youtubeEmbedUrl(video, window.location.origin)}
          title={t('web:exerciseLibrary.theatre.youtube.frameTitle', { label })}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox"
          onLoad={() => {
            // The player's side of the protocol starts once the page says it is listening; from
            // then on it posts its state and its errors, and `youtubeRefusal` reads the one that
            // matters here.
            frame.current?.contentWindow?.postMessage(
              JSON.stringify({ event: 'listening', id: video.id, channel: 'widget' }),
              YOUTUBE_EMBED_ORIGIN,
            );
          }}
        />
      )}

      {/* Which film this is, on the top edge: the foot belongs to the player's own controls. */}
      {!refused && (
        <div className={styles.top}>
          <span className={styles.chip}>
            <span className={styles.chipMark}>
              <LinkBrandMark brand="youtube" className={styles.chipGlyph} />
            </span>
            <span className={styles.chipLabel}>{label}</span>
            <span className={styles.chipHost}>YouTube</span>
          </span>
        </div>
      )}
    </div>
  );
}
